import type { FormProps } from 'antd/es/form';
import {
  useCalculPrimeConcessionnaire,
  useNouveauDossierConcessionnaireMutation,
} from 'hooks/dossiers.hooks';
import {
  ADEME_VEHICLE_NOT_FOUND_VALUE,
  ClientType,
  NouveauDossierConcessionnaireData,
  NouveauDossierConcessionnaireParams,
  YesOrNo,
} from 'types/Concessionnaire';
import { appendDossierFormDataDocuments } from 'utils/dossier';

type FormValues = Parameters<NonNullable<FormProps['onFinish']>>[0];

interface Args {
  state: Partial<NouveauDossierConcessionnaireData>;
  setState: React.Dispatch<
    React.SetStateAction<Partial<NouveauDossierConcessionnaireData>>
  >;
}

function isNotFoundAdemeValue(value?: string) {
  return value === ADEME_VEHICLE_NOT_FOUND_VALUE;
}

function getAdemeVehiclePayload(
  values: Partial<NouveauDossierConcessionnaireData>,
): Pick<
  NouveauDossierConcessionnaireParams,
  'ademeMarque' | 'ademeModele' | 'ademeTypeVariante' | 'ademeVehicleNotFound'
> {
  const ademeVehicleNotFound =
    values.ademeVehicleNotFound ||
    isNotFoundAdemeValue(values.ademeMarque) ||
    isNotFoundAdemeValue(values.ademeModele) ||
    isNotFoundAdemeValue(values.ademeTypeVariante);

  if (ademeVehicleNotFound) {
    return {
      ademeMarque: undefined,
      ademeModele: undefined,
      ademeTypeVariante: undefined,
      ademeVehicleNotFound: true,
    };
  }

  const hasFullAdemeVehicle =
    !!values.ademeMarque && !!values.ademeModele && !!values.ademeTypeVariante;

  return {
    ademeMarque: values.ademeMarque,
    ademeModele: values.ademeModele,
    ademeTypeVariante: values.ademeTypeVariante,
    ademeVehicleNotFound: hasFullAdemeVehicle ? false : undefined,
  };
}

export interface UseSubmitNouveauDossierConcessionnaireReturn {
  submitStep1: (values: FormValues) => void;
  submitStep2: (values: FormValues) => Promise<boolean>;
  buildFinalSubmitVariables: (
    values: FormValues,
  ) => { particulier: boolean; formData: FormData } | undefined;
  step2Mutation: ReturnType<typeof useCalculPrimeConcessionnaire>;
  step3Mutation: ReturnType<typeof useNouveauDossierConcessionnaireMutation>;
  isPersonnePhysique: boolean;
}

export function useSubmitNouveauDossierConcessionnaire({
  state,
  setState,
}: Args): UseSubmitNouveauDossierConcessionnaireReturn {
  const step2Mutation = useCalculPrimeConcessionnaire();
  const step3Mutation = useNouveauDossierConcessionnaireMutation();

  function submitStep1(values: FormValues) {
    setState((prev) => ({ ...prev, ...values }));
  }

  async function submitStep2(values: FormValues) {
    const nextState = { ...state, ...values };
    const isPersonnePhysique =
      nextState.typeClientFinal === ClientType.PERSONNE_PHYSIQUE;
    let ficheDeLiaisonId: string | undefined;

    try {
      if (!isPersonnePhysique) {
        ficheDeLiaisonId = nextState.ficheDeLiaison?.id;

        if (!ficheDeLiaisonId) {
          console.error(
            new Error('Fiche de liaison manquante pour le calcul de la prime'),
          );
          return false;
        }
      }

      const result = await step2Mutation.mutateAsync(
        isPersonnePhysique
          ? {
              typeClientFinal: nextState.typeClientFinal!,
              categorieVehicule: values.categorieVehicule,
              scoreEnvironnementalAtteint: values.scoreEnvironnementalAtteint
                ? values.scoreEnvironnementalAtteint === YesOrNo.OUI
                : undefined,
              coutAcquisitionInferieur47000:
                values.coutAcquisitionInferieur47000
                  ? values.coutAcquisitionInferieur47000 === YesOrNo.OUI
                  : undefined,
              masseVehiculeInferieure2400: values.masseVehiculeInferieure2400
                ? values.masseVehiculeInferieure2400 === YesOrNo.OUI
                : undefined,
              dejaBeneficieBonusEcologique: values.dejaBeneficieBonusEcologique
                ? values.dejaBeneficieBonusEcologique === YesOrNo.OUI
                : undefined,
              produitEnEurope: values.produitEnEurope
                ? values.produitEnEurope === YesOrNo.OUI
                : undefined,
              vehiculeAdeme: values.vehiculeAdeme
                ? values.vehiculeAdeme === YesOrNo.OUI
                : undefined,
              grosRouleur: values.grosRouleur
                ? values.grosRouleur === YesOrNo.OUI
                : undefined,
              datePrevisionnelleDevis:
                values.datePrevisionnelleDevis?.format('YYYY-MM-DD')!,
              codePostal: nextState.codePostal!,
              usageVehiculeParticulier: values.usageVehiculeParticulier,
              statutPrecarite: nextState.statutPrecarite!,
              revenuFiscalReference: nextState.revenuFiscalReference,
              nombreParts: nextState.nombreParts,
              decile: nextState.decile,
            }
          : {
              typeClientFinal: nextState.typeClientFinal!,
              categorieVehicule: values.categorieVehicule,
              typeAcheteur: nextState.typeAcheteur!,
              ficheDeLiaisonId: ficheDeLiaisonId!,
              usageVehicules: values.usageVehicules,
              produitEnEurope: values.produitEnEurope
                ? values.produitEnEurope === YesOrNo.OUI
                : undefined,
              ...getAdemeVehiclePayload(values),
            },
      );

      setState((prev) => ({
        ...prev,
        ...values,
        primeParVehicule: result.primeParVehicule,
        primeParGwh: result.primeParGwh,
        volume: result.volume,
      }));

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  function buildFinalSubmitVariables(values: FormValues) {
    const payload = {
      ...state,
      ...values,
    } as NouveauDossierConcessionnaireData;

    try {
      const formData = new FormData();
      const {
        dateEditionDevis,
        dateDevis,
        dateLivraison,
        datePrevisionnelleDevis,
        scoreEnvironnementalAtteint,
        coutAcquisitionInferieur47000,
        masseVehiculeInferieure2400,
        dejaBeneficieBonusEcologique,
        produitEnEurope,
        vehiculeAdeme,
        grosRouleur,
        ademeMarque,
        ademeModele,
        ademeTypeVariante,
        ademeVehicleNotFound,
        ...rest
      } = payload;
      const data: NouveauDossierConcessionnaireParams = {
        ...rest,
        dateEditionDevis: dateEditionDevis?.format('YYYY-MM-DD'),
        dateDevis: dateDevis?.format('YYYY-MM-DD'),
        datePrevisionnelleDevis: datePrevisionnelleDevis?.format('YYYY-MM-DD'),
        dateLivraison: dateLivraison.format('YYYY-MM-DD'),
        scoreEnvironnementalAtteint: scoreEnvironnementalAtteint
          ? scoreEnvironnementalAtteint === YesOrNo.OUI
          : undefined,
        coutAcquisitionInferieur47000: coutAcquisitionInferieur47000
          ? coutAcquisitionInferieur47000 === YesOrNo.OUI
          : undefined,
        masseVehiculeInferieure2400: masseVehiculeInferieure2400
          ? masseVehiculeInferieure2400 === YesOrNo.OUI
          : undefined,
        dejaBeneficieBonusEcologique: dejaBeneficieBonusEcologique
          ? dejaBeneficieBonusEcologique === YesOrNo.OUI
          : undefined,
        produitEnEurope: produitEnEurope
          ? produitEnEurope === YesOrNo.OUI
          : undefined,
        vehiculeAdeme: vehiculeAdeme
          ? vehiculeAdeme === YesOrNo.OUI
          : undefined,
        grosRouleur: grosRouleur ? grosRouleur === YesOrNo.OUI : undefined,
        ...getAdemeVehiclePayload({
          ademeMarque,
          ademeModele,
          ademeTypeVariante,
          ademeVehicleNotFound,
        }),
        ficheDeLiaisonId: state.ficheDeLiaison?.id,
        rai: state.ficheDeLiaison?.rai,
      };

      formData.append('data', JSON.stringify(data));

      appendDossierFormDataDocuments(payload.devis, formData, [
        'pièce just. de début des travaux',
      ]);

      if (payload.typeClientFinal === ClientType.PERSONNE_PHYSIQUE) {
        appendDossierFormDataDocuments(payload.avisImposition, formData, [
          'autre',
        ]);
      }
      appendDossierFormDataDocuments(payload?.otherFiles ?? [], formData, [
        'autre',
      ]);

      return {
        particulier: payload.typeClientFinal === ClientType.PERSONNE_PHYSIQUE,
        formData,
      };
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  return {
    submitStep1,
    submitStep2,
    buildFinalSubmitVariables,
    step2Mutation,
    step3Mutation,
    isPersonnePhysique: state.typeClientFinal === ClientType.PERSONNE_PHYSIQUE,
  };
}
